import { SortDirection } from "@redotech/congo-enums/sort-direction";
import { WithdrawalListView } from "@redotech/merchant-sdk/rpc/schema/withdrawals/list-withdrawals";
import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

export function withdrawalNeedsActionCountQueryKey(teamId: string) {
  return ["withdrawal-needs-action-count", teamId] as const;
}

/** How many withdrawals wait on the merchant, shared by the side nav and the home page. */
export function useWithdrawalNeedsActionCount(): number | undefined {
  const team = useRequiredContext(TeamContext);
  const query = useRpcQuery(RedoMerchantRpcClientContext, {
    method: "listWithdrawals",
    args: {
      view: WithdrawalListView.NEEDS_ACTION,
      pageSize: 1,
      offset: 0,
      sortDirection: SortDirection.Desc,
    },
    options: {
      queryKey: withdrawalNeedsActionCountQueryKey(team._id),
      placeholderData: keepPreviousData,
    },
  });
  return query.data?.total;
}

/** Acting on a withdrawal changes the count, so every page change refreshes it. */
export function useRefreshWithdrawalCountOnNavigate(): void {
  const team = useRequiredContext(TeamContext);
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const lastPathname = useRef(pathname);
  useEffect(() => {
    if (lastPathname.current === pathname) {
      return;
    }
    lastPathname.current = pathname;
    void queryClient.invalidateQueries({
      queryKey: withdrawalNeedsActionCountQueryKey(team._id),
    });
  }, [pathname, queryClient, team._id]);
}
